import React, { useCallback, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadCloud, FileText, AlertTriangle, CheckCircle2, ArrowRight, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { parseTRDate, parseTRAmount, normalizeSheetDates } from "@/lib/excelDate";

// Beklenen sütun sırası (0-based indeks):
//   A(0)=Tarih, B(1)=Tutar, C(2)=Açıklama (ops), D(3)=Kategori (ops),
//   E(4)=Cari (ops), F(5)=Vade Tarihi (ops)
const COLS = {
  date: 0, amount: 1, description: 2, category: 3, contact_name: 4, due_date: 5,
};

const stripApos = (v) => String(v ?? "").replace(/^'+/, "").trim();

function downloadTemplate(txType) {
  const wb = XLSX.utils.book_new();
  const label = txType === "income" ? "Tahsilat" : "Ödeme";
  const headers = [
    "Tarih (GG.AA.YYYY)", "Tutar (TL)",
    "Açıklama (opsiyonel)", "Kategori (opsiyonel)",
    "Cari (opsiyonel)", "Vade Tarihi (opsiyonel)",
  ];
  const sample = txType === "income"
    ? [
        ["01.02.2026", "15000,00", "Nakit tahsilat", "Satış", "ABC Ticaret Ltd.", ""],
        ["10.02.2026", "8500,50", "Havale", "Satış", "XYZ Tedarik A.Ş.", ""],
      ]
    : [
        ["01.02.2026", "5200,00", "Kira ödemesi", "Kira", "", ""],
        ["10.02.2026", "1250,75", "Elektrik", "Fatura", "", ""],
      ];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
  ws["!cols"] = headers.map(() => ({ wch: 22 }));
  XLSX.utils.book_append_sheet(wb, ws, label);
  XLSX.writeFile(wb, `${txType === "income" ? "tahsilat" : "odeme"}-sablonu.xlsx`);
}

export default function TransactionImportModal({ txType, accounts, onClose, onImported }) {
  const [step, setStep] = useState("upload");
  const [rows, setRows] = useState([]);
  const [accountId, setAccountId] = useState(accounts?.[0]?.id || "");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const isIncome = txType === "income";
  const labelKind = isIncome ? "Tahsilat" : "Ödeme";
  const modalTitle = isIncome ? "Tahsilat / Gelir İçe Aktar" : "Ödeme / Gider İçe Aktar";

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: "array", codepage: 65001, raw: true, cellNF: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        normalizeSheetDates(ws);
        const arr = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "", raw: false, dateNF: "dd.mm.yyyy" });
        if (!arr.length) { toast.error("Dosya boş"); return; }

        // Başlık satırı algıla
        let startIdx = 0;
        const first = (arr[0] || []).map((x) => String(x || "").toLowerCase());
        if (first.some((c) => c.includes("tarih") || c.includes("tutar") || c.includes("açıklama") || c.includes("aciklama"))) {
          startIdx = 1;
        }

        const parsed = [];
        for (let i = startIdx; i < arr.length; i++) {
          const r = arr[i];
          const dateRaw = r[COLS.date];
          const amountRaw = r[COLS.amount];
          if (!dateRaw && !amountRaw) continue;

          const description = String(r[COLS.description] || "").trim();
          const category = String(r[COLS.category] || "").trim();
          const contact = stripApos(r[COLS.contact_name] || "");
          const dueRaw = r[COLS.due_date];

          const dateISO = parseTRDate(dateRaw);
          const amt = parseTRAmount(amountRaw);
          const dueISO = dueRaw ? parseTRDate(dueRaw) : null;

          let err = "";
          if (!dateISO) err = "Tarih geçersiz";
          else if (isNaN(amt) || amt <= 0) err = "Tutar geçersiz";

          parsed.push({
            date: dateISO, date_raw: String(dateRaw || ""),
            amount: isNaN(amt) ? null : amt,
            amount_raw: String(amountRaw || ""),
            description, category,
            contact_name: contact,
            due_date: dueISO, due_date_raw: String(dueRaw || ""),
            _error: err,
          });
        }
        if (!parsed.length) { toast.error("Aktarılabilecek satır bulunamadı"); return; }
        setRows(parsed);
        setStep("preview");
      } catch (e) {
        console.error(e);
        toast.error("Dosya okunamadı: " + (e?.message || ""));
      }
    };
    reader.onerror = () => toast.error("Dosya okunamadı");
    reader.readAsArrayBuffer(file);
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const invalid = rows.filter((r) => r._error).length;
    const withContact = rows.filter((r) => !r._error && r.contact_name).length;
    const totalAmount = rows.reduce((s, r) => s + (r._error ? 0 : Number(r.amount) || 0), 0);
    return { total, valid: total - invalid, invalid, withContact, totalAmount };
  }, [rows]);

  const submit = async () => {
    if (!accountId) { toast.error("Lütfen hedef hesabı seçin"); return; }
    const items = rows.filter((r) => !r._error).map((r) => ({
      date: r.date,
      amount: r.amount,
      description: r.description || null,
      category: r.category || null,
      contact_name: r.contact_name || null,
      due_date: r.due_date || null,
    }));
    if (!items.length) { toast.error("Aktarılacak geçerli satır yok"); return; }
    setUploading(true);
    try {
      const { data } = await api.post("/transactions/bulk-import-json", {
        tx_type: txType,
        account_id: accountId,
        auto_fifo: true,
        items,
      });
      setResult(data);
      setStep("result");
      toast.success(`${data.imported} ${labelKind.toLowerCase()} aktarıldı`);
      onImported?.();
    } catch (e) {
      toast.error("Aktarım hatası: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="tx-import-modal">
      <div className="brutal-card bg-white w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
          <div className="flex items-center gap-2">
            <FileText size={18} />
            <h2 className="font-display text-xl font-bold">{modalTitle}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-black/5" data-testid="tx-import-close">
            <X size={18} />
          </button>
        </div>

        {step === "upload" && (
          <div className="p-6 space-y-4">
            <div>
              <div className="label-caps mb-1">Hedef Banka Hesabı</div>
              <select
                data-testid="tx-import-account"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="border border-black/20 px-3 py-2 min-w-[280px] bg-white text-sm"
              >
                {accounts?.length === 0 && <option value="">Hesap tanımlı değil</option>}
                {accounts?.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1">
                Aktarılan {labelKind.toLowerCase()} kayıtları bu hesabın bakiyesini etkileyecek.
              </p>
            </div>

            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
              className="border-2 border-dashed border-black/30 p-10 text-center cursor-pointer hover:bg-neutral-50"
              data-testid="tx-import-dropzone"
            >
              <UploadCloud size={44} className="mx-auto text-neutral-500" />
              <p className="mt-3 font-semibold">Excel veya CSV dosyası seçin ya da sürükleyin</p>
              <p className="text-xs text-neutral-500 mt-1">.xlsx / .xls / .csv</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="tx-import-file"
            />

            <div className="flex items-center gap-2">
              <button
                onClick={() => downloadTemplate(txType)}
                data-testid="tx-import-template"
                className="brutal-btn bg-white text-black border border-black px-3 py-1.5 text-xs flex items-center gap-1.5"
              >
                <Download size={12} /> Örnek Şablon İndir (.xlsx)
              </button>
              <span className="text-xs text-neutral-500">Önerilir: önce şablonu indir, doldur, yükle</span>
            </div>

            <div className="bg-neutral-50 border border-black/10 p-4 text-xs">
              <p className="font-semibold uppercase tracking-widest mb-2">Beklenen sütun düzeni (bu sırada)</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-neutral-700">
                <div><b>A</b> — Tarih (GG.AA.YYYY)</div>
                <div><b>D</b> — Kategori (ops.)</div>
                <div><b>B</b> — Tutar</div>
                <div><b>E</b> — Cari (ops.)</div>
                <div><b>C</b> — Açıklama (ops.)</div>
                <div><b>F</b> — Vade Tarihi (ops.)</div>
              </div>
              <p className="text-neutral-500 mt-2">
                Başlık satırı otomatik atlanır. Cari adı verilirse (varsa) sistemde otomatik eşleştirilir ve
                <b> otomatik FIFO fatura kapama</b> tetiklenir. Türkçe tarih (17.07.2026) ve virgüllü tutar
                (15.000,00) desteklenir.
              </p>
            </div>
          </div>
        )}

        {step === "preview" && (
          <>
            <div className="p-4 border-b border-black/10 text-sm">
              <span className="label-caps mr-2">Okunan:</span>
              <span className="font-mono-num font-semibold">{stats.total}</span>
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Geçerli:</span>
              <span className="font-mono-num font-semibold text-emerald-700">{stats.valid}</span>
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Cari Adı Var:</span>
              <span className="font-mono-num font-semibold">{stats.withContact}</span>
              {stats.invalid > 0 && (
                <>
                  <span className="mx-3 text-neutral-300">|</span>
                  <span className="label-caps mr-2">Hatalı:</span>
                  <span className="font-mono-num font-semibold text-rose-600">{stats.invalid}</span>
                </>
              )}
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Toplam Tutar:</span>
              <span className={`font-mono-num font-semibold ${isIncome ? "text-emerald-700" : "text-rose-600"}`}>
                {stats.totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
              </span>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs" data-testid="tx-import-preview-table">
                <thead className="bg-neutral-50 sticky top-0 z-10">
                  <tr className="border-b-2 border-black/80">
                    <th className="text-left px-3 py-2 label-caps">Tarih</th>
                    <th className="text-right px-3 py-2 label-caps">Tutar</th>
                    <th className="text-left px-3 py-2 label-caps">Açıklama</th>
                    <th className="text-left px-3 py-2 label-caps">Kategori</th>
                    <th className="text-left px-3 py-2 label-caps">Cari</th>
                    <th className="text-left px-3 py-2 label-caps">Vade</th>
                    <th className="text-left px-3 py-2 label-caps">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b border-black/5 ${r._error ? "bg-rose-50" : ""}`}>
                      <td className="px-3 py-1.5 font-mono-num">{r.date || <span className="text-rose-600">{r.date_raw}</span>}</td>
                      <td className={`px-3 py-1.5 text-right font-mono-num ${isIncome ? "text-emerald-700" : "text-rose-600"}`}>
                        {r.amount != null
                          ? Number(r.amount).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : <span className="text-rose-600">{r.amount_raw}</span>}
                      </td>
                      <td className="px-3 py-1.5">{r.description || "—"}</td>
                      <td className="px-3 py-1.5">{r.category || "—"}</td>
                      <td className="px-3 py-1.5">{r.contact_name || "—"}</td>
                      <td className="px-3 py-1.5 font-mono-num">{r.due_date || (r.due_date_raw ? <span className="text-rose-600">{r.due_date_raw}</span> : "—")}</td>
                      <td className="px-3 py-1.5">
                        {r._error
                          ? <span className="text-rose-700 text-[10px] uppercase tracking-widest flex items-center gap-1"><AlertTriangle size={10} /> {r._error}</span>
                          : <span className="text-emerald-700 text-[10px] uppercase tracking-widest">Hazır</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-black/10 p-4 flex items-center justify-between gap-2">
              <button
                onClick={() => { setRows([]); setStep("upload"); }}
                className="brutal-btn bg-white text-black border border-black px-4 py-2 text-sm"
                data-testid="tx-import-back"
              >
                Geri
              </button>
              <button
                onClick={submit}
                disabled={uploading || stats.valid === 0 || !accountId}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
                data-testid="tx-import-submit"
              >
                {uploading ? "Aktarılıyor…" : `${stats.valid} Kaydı Aktar`}
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}

        {step === "result" && result && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={40} className="text-emerald-600" />
              <div>
                <h3 className="font-display text-2xl font-bold">Aktarım Tamamlandı</h3>
                <p className="text-sm text-neutral-600">Sonuç aşağıda özetlenmiştir.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <RStat label="Aktarılan" value={result.imported} tone="emerald" />
              <RStat label="Cari Eşleşen" value={result.matched_contacts} tone="emerald" />
              <RStat label="Fatura Kapandı (FIFO)" value={result.invoices_auto_closed} tone="emerald" />
              <RStat label="Hatalı" value={result.error_count} tone={result.error_count > 0 ? "rose" : ""} />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="max-h-56 overflow-auto border border-rose-200 bg-rose-50 p-3">
                <p className="font-semibold text-xs uppercase tracking-widest text-rose-700 mb-2">Hatalar</p>
                <ul className="text-xs space-y-1 text-rose-800">
                  {result.errors.map((e, i) => <li key={i}>Satır {e.row}: {e.message}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm"
                data-testid="tx-import-done"
              >
                Kapat
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RStat({ label, value, tone }) {
  const cls = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "text-black";
  return (
    <div className="border-2 border-black p-3">
      <p className="label-caps text-[10px]">{label}</p>
      <p className={`font-mono-num text-2xl font-bold mt-1 ${cls}`}>{value ?? 0}</p>
    </div>
  );
}
